import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Pagination, Modal, Button } from "react-bootstrap";

import Link from "components/Button/Link";
import NotFound from "components/not-found/NotFound";
import useFullPageLoader from "hooks/useFullPageLoader";
import useMuiButtonLoader from "hooks/useMuiButtonLoader";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "components/Button/IconButton";

import { deleteFileFromInterviewFolder, getInterviewFolders } from "services/interview/interview";
import ModalBox from "components/modal/ModalBox";
import { Input } from "components/Form";

export default function ViewInterviewDocuments() {
    const { name } = useParams();

    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [folders, setFolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [currentPrefix, setCurrentPrefix] = useState("InterviewVideos/");

    const [activePage, setActivePage] = useState(1);
    const [limit] = useState(10);
    const [total] = useState(0);

    const [submitButtonElement, setSubmitLoading] = useMuiButtonLoader();
    const [loader, showLoader, hideLoader] = useFullPageLoader();

    // ================= MODAL STATE =================
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    // ================= FETCH FOLDER DATA =================
    const fetchFolders = async (prefix = currentPrefix) => {
        showLoader();
        setSubmitLoading(true);

        try {
            const res = await getInterviewFolders({ prefix, platformType: "Website" });
            if (res.code === 200) {
                setFolders(res.data.folders || []);
                setFiles(res.data.files || []);
                setBreadcrumbs(res.data.breadcrumbs || []);
                setCurrentPrefix(res.data.currentPath);
            } else {
                toast.error(res.message);
            }
        } catch (err) {
            toast.error(err?.message || "Something went wrong");
        }

        setSubmitLoading(false);
        hideLoader();
    };

    // ================= INITIAL LOAD =================
    useEffect(() => {
        fetchFolders("InterviewVideos/");
    }, []);

    // ================= HELPERS =================
    const isVideoFile = (fileName = "") => fileName.toLowerCase().endsWith(".mp4");

    const canDeleteFile = (fileURL) => {
        const parts = fileURL.split("/").map((p) => p.toLowerCase());
        return !parts.includes("final");
    };

    // ================= DELETE FILE =================
    const handleDeleteFile = async () => {
        if (!fileToDelete) return;

        try {
            showLoader();
            setSubmitLoading(true);

            const documentPath = currentPrefix;

            const res = await deleteFileFromInterviewFolder({
                filename: fileToDelete.fileName,
                document: documentPath,
            });

            if (res.code === 200) {
                toast.success("File deleted successfully");
                fetchFolders(currentPrefix);
            } else {
                toast.error(res.message || "Failed to delete file");
            }
        } catch (err) {
            toast.error(err?.message || "Something went wrong");
        } finally {
            setSubmitLoading(false);
            hideLoader();
            setShowConfirmModal(false);
            setFileToDelete(null);
        }
    };

    const openConfirmModal = (file) => {
        if (!canDeleteFile(file.fileURL)) {
            toast.error("Files in Final folder cannot be deleted");
            return;
        }
        setFileToDelete(file);
        setShowConfirmModal(true);
    };

    const closeConfirmModal = () => {
        setShowConfirmModal(false);
        setFileToDelete(null);
    };

    const filteredFolders = folders.filter(folder =>
        folder.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredFiles = files.filter(file =>
        file.fileName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    // ================= RENDER =================

    const resetFilter = () => {
        setSearchTerm("");
    };
    return (
        <div className="WrapperBox">
            <div className="Small-Wrapper">
                {/* HEADER */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <h4 className="Title" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        Interview Videos From Storage

                    </h4>

                    {/* BREADCRUMBS */}
                    <div style={{ fontSize: "14px", color: "#555" }}>
                        {breadcrumbs.map((crumb, index) => (
                            <span key={index}>
                                <span
                                    onClick={() => fetchFolders(crumb.path)}
                                    style={{ cursor: "pointer", color: "#0d6efd", fontWeight: 500 }}
                                >
                                    {crumb.name}
                                </span>
                                {index < breadcrumbs.length - 1 && <span style={{ margin: "0 6px" }}>{">"}</span>}
                            </span>
                        ))}
                    </div>
                </div>

                <hr className="border border-danger" />

                {loader}

                {/* SEARCH INPUT */}


                <div className="SearchBox" style={{ marginBottom: "14px", marginTop: "2px" }}>
                    <div>
                        <Input
                            type="text"
                            placeholder="Search files and folders..."
                            className="form-control"
                            name="search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: "400px" }}
                        />
                    </div>

                    <Button
                        className="refresh_btn mx-2"
                        onClick={resetFilter}
                        style={{
                            backgroundColor: "white",
                            color: "#0d6efd",
                            border: "1px solid #0d6efd",
                            outline: "none",
                            boxShadow: "none",
                        }}
                        onFocus={(e) => {
                            e.target.style.outline = "none";
                            e.target.style.boxShadow = "none";
                        }}
                        onBlur={(e) => {
                            e.target.style.outline = "none";
                            e.target.style.boxShadow = "none";
                        }}
                    >
                        <i className="fa fa-refresh"></i>
                    </Button>




                </div>


                {/* TABLE */}
                <div className="TableList table-responsive p-0">
                    <table className="position-relative">
                        <thead>
                            <tr>
                                <th style={{ width: "70px", textAlign: "center" }}>S.No</th>
                                <th style={{ textAlign: "left" }}>Name</th>
                                <th>Download</th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            {/* FOLDERS */}
                            {filteredFolders.map((folder, index) => (
                                <tr
                                    key={`folder-${index}`}
                                    onClick={() => fetchFolders(folder.path)}
                                    style={{ cursor: "pointer", backgroundColor: "#f8f9fa", fontWeight: 600 }}
                                >
                                    <td style={{ textAlign: "center" }}>{index + 1}</td>
                                    <td style={{ textAlign: "left" }}>📁 &nbsp;{folder.name}</td>
                                    <td>-</td>
                                    <td></td>
                                </tr>
                            ))}

                            {/* FILES */}
                            {filteredFiles.map((file, index) => (
                                <tr key={`file-${index}`}>
                                    <td style={{ textAlign: "center" }}>{folders.length + index + 1}</td>

                                    <td style={{ textAlign: "left" }}>
                                        <span
                                            style={{
                                                color: "#0d6efd",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                            }}
                                            onClick={() => window.open(file.fileURL, "_blank")}
                                        >
                                            {isVideoFile(file.fileName) ? (
                                                <OndemandVideoIcon fontSize="small" sx={{ color: "#e5533d" }} />
                                            ) : (
                                                "📄"
                                            )}
                                            &nbsp;{file.fileName}
                                        </span>
                                    </td>

                                    <td style={{ textAlign: "left" }}>
                                        <IconButton
                                            onClick={() => window.open(file.fileURL, "_blank")}
                                            size="small"
                                            sx={{ color: "#4971b2" }}
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </td>

                                    <IconButton
                                        onClick={() => openConfirmModal(file)}
                                        size="small"
                                        disabled={!canDeleteFile(file.fileURL)}
                                        sx={{
                                            color: canDeleteFile(file.fileURL) ? "#e5533d" : "#ffffffff",
                                            cursor: canDeleteFile(file.fileURL) ? "pointer" : "not-allowed",

                                            "&.Mui-disabled": {
                                                color: "transparent",   // 👈 force disabled color
                                                opacity: 0.1,          // optional (MUI usually dims disabled)
                                            },
                                        }}
                                        type="acl"
                                        role={130607}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>

                                </tr>
                            ))}

                            {filteredFolders.length === 0 && filteredFiles.length === 0 && <NotFound cols={12} />}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {total >= limit && (
                    <div className="Pagination">
                        <Pagination
                            activePage={activePage}
                            itemsCountPerPage={limit}
                            totalItemsCount={total}
                            pageRangeDisplayed={5}
                            onChange={(page) => setActivePage(page)}
                        />
                    </div>
                )}

                {/* ================= CONFIRMATION MODAL ================= */}

                <ModalBox
                    status={showConfirmModal}
                    onClose={closeConfirmModal}
                    title={"Delete File"}
                    size={"sm"}
                    description={`Are you sure you want to delete ${fileToDelete?.fileName}`}
                    customizedFooter={
                        <Button onClick={handleDeleteFile} {...submitButtonElement} style={{ backgroundColor: "#e5533d", border: "1px solid #e5533d" }}>
                            Delete
                        </Button>
                    }
                />

            </div>
        </div>
    );
}
